import { useTranslation } from "react-i18next";
import Modal from "../Modal/Modal";
import {
  MdCheck,
  MdClose,
  MdMoreHoriz,
  MdOutlineAttachment,
  MdOutlineSelectAll,
} from "react-icons/md";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import clsx from "clsx";
import RadioGroup from "../RadioGroup/RadioGroup";
import CustomSelect, { ECustomSelectIconType } from "../Select/CustomSelect";
import CheckBox from "../CheckBox/CheckBox";
import TextInput from "../TextInput/TextInput";
import { useEffect, useMemo, useState } from "react";
import useMobile from "src/hooks/useMobile";
import useCollapsibles from "src/hooks/useCollapsibles";
import InfoBlock from "../InfoBlock/InfoBlock";
import { formatLongDate } from "src/helpers/utils";
import useMainFilter from "src/hooks/useMainFilter";
import useCurrentUser from "src/hooks/useCurrentUser";
import ModalFooterMessage, {
  EModalMessageKind,
} from "../Modal/ModalFooterMessage";
import useUiPreferences from "src/store/useUiPreferences";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

interface IExportTransactionsModal {
  onClose: () => void;
  onConfirm: (data: any) => void;
  isLoading: boolean;
  selectedRows: number;
  totalRows: number;
}

export enum EXPORT_TYPE {
  ALL = "ALL",
  SELECTION = "SELECTION",
}

export enum FILE_TYPES {
  XLSX = "XLSX",
  PDF = "PDF",
  JSON = "JSON",
  CSV = "CSV",
}

const ExportTransactionsModal: React.FC<IExportTransactionsModal> = ({
  onClose,
  onConfirm,
  isLoading,
  selectedRows = 0,
  totalRows = 0,
}) => {
  const { t } = useTranslation(["modals", "common"]);
  const exportUiPreferences = useUiPreferences((s) => s.export);
  const setUiPreferences = useUiPreferences((s) => s.setUiPreferences);

  const { updateCollapsible, collapsiblePreferences } = useCollapsibles();
  const { mainFilter } = useMainFilter();
  const { isMobile } = useMobile();
  const [exportType, setExportType] = useState<EXPORT_TYPE>(
    exportUiPreferences.type,
  );
  const [formatNumbers, setFormatNumbers] = useState<boolean>(
    exportUiPreferences.formatNumbers,
  );
  const [csvColumnDelimiter, setCsvColumnDelimiter] = useState<string>(
    exportUiPreferences.fileFormatOptions[FILE_TYPES.CSV].columnDelimiter,
  );
  const [csvRowDelimiter, setCsvRowDelimiter] = useState<string>(
    exportUiPreferences.fileFormatOptions[FILE_TYPES.CSV].rowDelimiter,
  );
  const { getAccountById } = useCurrentUser();

  const getAccountName = useMemo(() => {
    return getAccountById(mainFilter?.account)?.name || "";
  }, [mainFilter?.account]);

  const [includeCurrencySymbol, setIncludeCurrencySymbol] = useState<boolean>(
    exportUiPreferences.includeCurrencySymbol,
  );
  const [fileFormat, setFileFormat] = useState<Partial<FILE_TYPES>>(
    exportUiPreferences.format,
  );
  const [fileName, setFileName] = useState<string>(
    `${getAccountName} - ${t("modals:export_transactions.file.defaultName")} - ${formatLongDate(
      new Date().toISOString(),
    )}`,
  );

  const canExport = useMemo(() => {
    const exportTypeCondition =
      exportType === EXPORT_TYPE.SELECTION ? selectedRows > 0 : totalRows > 0;
    return (
      exportType && fileFormat && fileName?.trim?.() && exportTypeCondition
    );
  }, [exportType, fileFormat, fileName]);

  const modalFooterMessage = useMemo(() => {
    if (exportType === EXPORT_TYPE.SELECTION) {
      if (selectedRows === 0) {
        return {
          message: t("modals:export_transactions.no_selected_rows"),
          kind: EModalMessageKind.ERROR,
        };
      } else {
        return {
          message: t("modals:export_transactions.rows_to_export", {
            count: selectedRows,
          }),
          kind: EModalMessageKind.INFO,
        };
      }
    }

    if (exportType === EXPORT_TYPE.ALL) {
      if (totalRows === 0) {
        return {
          message: t("modals:export_transactions.no_data_to_export"),
          kind: EModalMessageKind.ERROR,
        };
      } else {
        return {
          message: t("modals:export_transactions.rows_to_export", {
            count: totalRows,
          }),
          kind: EModalMessageKind.INFO,
        };
      }
    }

    return null;
  }, [t, totalRows, selectedRows, exportType]);

  useEffect(() => {
    setUiPreferences({
      export: {
        type: exportType,
        format: fileFormat,
        formatNumbers,
        includeCurrencySymbol,
        fileFormatOptions: {
          [FILE_TYPES.CSV]: {
            columnDelimiter: csvColumnDelimiter,
            rowDelimiter: csvRowDelimiter,
          },
        },
      },
    });
  }, [
    exportType,
    fileFormat,
    csvColumnDelimiter,
    csvRowDelimiter,
    formatNumbers,
    includeCurrencySymbol,
  ]);

  const fileFormatOptions = useMemo(() => {
    return [
      {
        value: FILE_TYPES.CSV,
        label: "CSV",
        icon: "MdAbc",
        iconType: ECustomSelectIconType.ICON,
      },
      {
        value: FILE_TYPES.XLSX,
        label: "Excel",
        icon: "MdGridOn",
        iconType: ECustomSelectIconType.ICON,
      },
      {
        value: FILE_TYPES.JSON,
        label: "JSON",
        icon: "MdDataArray",
        iconType: ECustomSelectIconType.ICON,
      },
    ];
  }, []);

  return (
    <Modal
      onEscape={() => onClose()}
      className="md:!w-[600px] md:!min-w-[600px] md:!max-w-[600px]"
    >
      <Modal.Header
        title={t("modals:export_transactions.title")}
        onClose={() => onClose()}
      />
      <Modal.Body>
        <div className={clsx(["flex", "flex-col", "gap-6", "w-full"])}>
          <CollapsibleBlock
            title={t("modals:export_transactions.what_to_export.title")}
            // open={collapsiblePreferences.exportTransactionsWhatToExportOpen}
            onChange={(isOpen: boolean) => {
              updateCollapsible({
                exportTransactionsWhatToExportOpen: isOpen,
              });
            }}
            blocked
            open
            icon={<MdOutlineSelectAll size={ICON_SIZES.SM} />}
          >
            <RadioGroup
              options={[
                {
                  description: t(
                    "modals:export_transactions.what_to_export.selection_description",
                  ),
                  value: EXPORT_TYPE.SELECTION,
                  label: t(
                    "modals:export_transactions.what_to_export.selection",
                  ),
                },
                {
                  description: t(
                    "modals:export_transactions.what_to_export.all_description",
                  ),
                  value: EXPORT_TYPE.ALL,
                  label: t("modals:export_transactions.what_to_export.all"),
                },
              ]}
              selected={exportType}
              onChange={(value: any) => setExportType(value)}
              columns={isMobile ? 1 : 2}
            />
          </CollapsibleBlock>

          <CollapsibleBlock
            title={t("modals:export_transactions.file.title")}
            // open={collapsiblePreferences.exportTransactionsFileInfoOpen}
            blocked
            open
            onChange={(isOpen: boolean) => {
              updateCollapsible({
                exportTransactionsFileInfoOpen: isOpen,
              });
            }}
            icon={<MdOutlineAttachment size={ICON_SIZES.SM} />}
          >
            <div
              className={clsx([
                "grid",
                "grid-cols-1",
                "md:grid-cols-2",
                "gap-4",
                "w-full",
              ])}
            >
              <CustomSelect
                onChange={(e: any) => setFileFormat(e?.value)}
                value={fileFormat}
                isClearable={false}
                isSearchable={false}
                withIcons
                options={fileFormatOptions}
                label={t("modals:export_transactions.file.format_label")}
                required
                placeholder={t(
                  "modals:export_transactions.file.format_placeholder",
                )}
              />
              <TextInput
                label={t("modals:export_transactions.file.name_label")}
                onChange={(e: any) => setFileName(e?.target?.value)}
                value={fileName}
                required
                placeholder={t(
                  "modals:export_transactions.file.name_placeholder",
                )}
              />
            </div>
          </CollapsibleBlock>

          <CollapsibleBlock
            title={t("modals:export_transactions.additional_options.title")}
            // open={
            //   collapsiblePreferences.exportTransactionsAdditionalOptionsOpen
            // }
            blocked
            open
            onChange={(isOpen: boolean) => {
              updateCollapsible({
                exportTransactionsAdditionalOptionsOpen: isOpen,
              });
            }}
            icon={<MdMoreHoriz size={ICON_SIZES.SM} />}
          >
            <div className={clsx(["flex", "w-full", "gap-4", "flex-col"])}>
              <div
                className={clsx([
                  "grid",
                  "grid-cols-1",
                  "md:grid-cols-2",
                  "gap-4",
                  "w-full",
                ])}
              >
                <CheckBox
                  checked={formatNumbers}
                  onChange={(isChecked: boolean) => setFormatNumbers(isChecked)}
                  label={t(
                    "modals:export_transactions.additional_options.numbers",
                  )}
                  description={t(
                    "modals:export_transactions.additional_options.numbers_description",
                  )}
                />
                <CheckBox
                  checked={includeCurrencySymbol}
                  onChange={(isChecked: boolean) =>
                    setIncludeCurrencySymbol(isChecked)
                  }
                  label={t(
                    "modals:export_transactions.additional_options.currency",
                  )}
                  description={t(
                    "modals:export_transactions.additional_options.currency_description",
                  )}
                />
              </div>
              {fileFormat === FILE_TYPES.CSV && (
                <>
                  <InfoBlock
                    displayMobileIcon
                    message={t(
                      "modals:export_transactions.additional_options.options_for_csv_message",
                    )}
                    title="Archivos CSV"
                  />
                  <div
                    className={clsx([
                      "grid",
                      "grid-cols-2",
                      "gap-4",
                      "md:grid-cols-2",
                      "w-full",
                    ])}
                  >
                    <TextInput
                      value={csvColumnDelimiter}
                      label={t(
                        "modals:export_transactions.additional_options.column_delimiter",
                      )}
                      onChange={(e: any) =>
                        setCsvColumnDelimiter(e?.target?.value)
                      }
                      required
                      placeholder={t(
                        "modals:export_transactions.additional_options.column_delimiter_placeholder",
                      )}
                    />
                    <TextInput
                      value={csvRowDelimiter}
                      label={t(
                        "modals:export_transactions.additional_options.row_delimiter",
                      )}
                      onChange={(e: any) =>
                        setCsvRowDelimiter(e?.target?.value)
                      }
                      placeholder={t(
                        "modals:export_transactions.additional_options.row_delimiter_placeholder",
                      )}
                      required
                    />
                  </div>
                </>
              )}
            </div>
          </CollapsibleBlock>
        </div>
      </Modal.Body>
      <Modal.Footer
        leftContent={
          modalFooterMessage ? (
            <ModalFooterMessage
              message={modalFooterMessage.message}
              kind={modalFooterMessage.kind}
            />
          ) : null
        }
        actions={[
          {
            text: t("common:buttons.export"),
            onClick: () => {
              onConfirm({
                exportType,
                includeCurrencySymbol,
                formatNumbers,
                fileName,
                fileFormat,
              });
            },
            kind: EButtonKind.SAVE,
            icon: <MdCheck size={ICON_SIZES.SM} />,
            disabled: !canExport,
          },
          {
            text: t("common:buttons.close"),
            onClick: () => onClose(),
            kind: EButtonKind.NORMAL,
            icon: <MdClose size={ICON_SIZES.SM} />,
          },
        ]}
      />
    </Modal>
  );
};

export default ExportTransactionsModal;
